import type { IconType } from 'react-icons';
import {
    LuCalendar,
    LuClock,
    LuFileText,
    LuHeart,
    LuHouse,
    LuSun,
    LuWallet,
} from 'react-icons/lu';
import { defineMessage, type MessageDescriptor } from 'react-intl';
import { FeatureFlags, type FeatureFlagKey } from '@/lib/feature-flag-constants';
import { route } from '@/routes/paths';

export type NavStage = 'manage' | 'explore';

export interface NavSection {
    id: string;
    label: MessageDescriptor;
    icon: IconType;
    group?: MessageDescriptor;
    path: string;

    childPaths?: string[];
    flag?: FeatureFlagKey;
    noSubheader?: boolean;
    tab?: boolean;
}

const labels = {
    home: defineMessage({ id: 'ad640b92-868d-497b-bfa9-12d6a099ce39', defaultMessage: 'Home' }),
    homeHub: defineMessage({ id: '2c8f4a19-7b06-4d53-9e21-8f5c0b3d7a46', defaultMessage: 'Home Hub' }),
    groupHub: defineMessage({ id: '6a3d0f52-8c17-4b94-a2e6-5f0b7c3d9e18', defaultMessage: 'Group Hub' }),
    savedListings: defineMessage({ id: '8b1d7c05-4e39-4a62-90f8-3c5b7a2e6d14', defaultMessage: 'Saved listings' }),
    finances: defineMessage({ id: '9d1e6b70-4c83-42a5-b7f8-0e3a5c9d2b14', defaultMessage: 'Finances' }),
    calendar: defineMessage({ id: '5a70c3e8-1f92-4b46-8d05-c7e2b9a4f603', defaultMessage: 'Calendar' }),
    settings: defineMessage({ id: 'f4b28d05-6a71-4e93-b0c2-9d5e1a8f7c36', defaultMessage: 'Settings' }),
    group: defineMessage({ id: '27ca433a-e593-425b-82e5-2796881473ec', defaultMessage: 'Group' }),
    bookings: defineMessage({ id: '0b699a26-e9a2-4a4d-924f-911caa3cef2b', defaultMessage: 'Home schedule' }),
    assets: defineMessage({ id: '605c3080-6ea0-4dd3-ae71-2ed8b06606a2', defaultMessage: 'Inventory' }),
    expenses: defineMessage({ id: '78766b74-f0ec-46be-9544-f1abcf8e9efa', defaultMessage: 'Expenses' }),
    reports: defineMessage({ id: 'f2b5d907-4c83-41ae-8d16-3b90e7c5a2f8', defaultMessage: 'Reports' }),
    documents: defineMessage({ id: '04db2fbd-5b29-4949-bb2c-18488c9bd0b8', defaultMessage: 'Documents' }),
    decisions: defineMessage({ id: 'a9e04b31-8f72-4c5d-b063-1e2a7d9c4f80', defaultMessage: 'Decision log' }),
    activity: defineMessage({ id: 'bb86cffe-2a0d-4bd6-b4f1-1ce86faf0ea2', defaultMessage: 'Activity' }),
    members: defineMessage({ id: '7c1a9f3e-b285-4d60-8e47-2f5c0a9b6d13', defaultMessage: 'Members' }),
    preferences: defineMessage({ id: 'c06f6021-11aa-498b-8208-5711083e0e9c', defaultMessage: 'Preferences' }),
    billing: defineMessage({ id: '3e7f2c81-9a04-4b56-8d1f-c5b0e6a7429d', defaultMessage: 'Plan & billing' }),
    help: defineMessage({ id: '9b0d5a72-1e38-4c94-a6f5-70d2b8e3c145', defaultMessage: 'Help' }),
} as const;

// Paths are unchanged; only the words are. The hub carries its own title in the
// hero, hence `noSubheader`.
const MANAGE_SECTIONS: NavSection[] = [
    { id: 'overview', label: labels.homeHub, icon: LuHouse, path: '', flag: FeatureFlags.HOME_OVERVIEW, noSubheader: true, tab: true },
    { id: 'activity', label: labels.activity, icon: LuClock, path: 'activity', flag: FeatureFlags.HOME_ACTIVITY, tab: true },
    { id: 'expenses', label: labels.finances, icon: LuWallet, path: 'expenses', childPaths: ['inventory'], flag: FeatureFlags.HOME_EXPENSES },
    { id: 'schedule', label: labels.calendar, icon: LuCalendar, path: 'schedule', childPaths: ['bookings'], flag: FeatureFlags.HOME_SCHEDULE, tab: true },
    { id: 'documents', label: labels.documents, icon: LuFileText, path: 'documents', flag: FeatureFlags.HOME_DOCUMENTS },
    { id: 'preferences', label: labels.settings, icon: LuSun, path: 'preferences', flag: FeatureFlags.HOME_PREFERENCES },
];

const EXPLORE_SECTIONS: NavSection[] = [
    { id: 'overview', label: labels.groupHub, icon: LuHouse, path: '', flag: FeatureFlags.GROUP_DETAILS, noSubheader: true, tab: true },
    { id: 'activity', label: labels.activity, icon: LuClock, path: 'activity', flag: FeatureFlags.GROUP_ACTIVITY, tab: true },
    { id: 'listings', label: labels.savedListings, icon: LuHeart, path: 'listings', flag: FeatureFlags.GROUP_DETAILS, tab: true },
    { id: 'preferences', label: labels.preferences, icon: LuSun, path: 'preferences', flag: FeatureFlags.GROUP_PREFERENCES },
];

export const NAV_SECTIONS: Record<NavStage, NavSection[]> = {
    manage: MANAGE_SECTIONS,
    explore: EXPLORE_SECTIONS,
};

export interface NavSecondaryItem {
    id: string;
    label: MessageDescriptor;
    icon: IconType;
    path: string;
    external?: boolean;
    flag?: FeatureFlagKey;
}

const secondaryItems: NavSecondaryItem[] = [
];

export const NAV_SECONDARY: Record<NavStage, NavSecondaryItem[]> = {
    manage: secondaryItems,
    explore: secondaryItems,
};

/**
 * Preferences panes used to derive the current page breadcrumb.
 * The preferences landing pages now provide navigation between panes.
 */
export interface NavPrefsPane {
    id: string;
    label: MessageDescriptor;
    path: string;
    flag?: FeatureFlagKey;
}

export const NAV_PREFS_PANES: Record<NavStage, NavPrefsPane[]> = {
    manage: [
        { id: 'general', label: defineMessage({ id: '1f7d3c6a-2b84-4e95-9a61-7c0d8e3f5b92', defaultMessage: 'Home profile' }), path: 'general', flag: FeatureFlags.HOME_PREFERENCES_GENERAL },
        { id: 'members', label: labels.members, path: 'members', flag: FeatureFlags.HOME_PREFERENCES_MEMBERS },
        { id: 'booking', label: defineMessage({ id: '4a7c0e93-2f81-4d5b-8607-c9e2a5f1b838', defaultMessage: 'Booking & scheduling' }), path: 'booking', flag: FeatureFlags.HOME_PREFERENCES_BOOKING },
        { id: 'finance', label: defineMessage({ id: '6b2d8f40-9e15-4a73-b8c6-01f4d7a3e592', defaultMessage: 'Costs & finance' }), path: 'finance', flag: FeatureFlags.HOME_PREFERENCES_FINANCE },
        { id: 'categories', label: defineMessage({ id: '7d4c6df6-6c29-429d-bb55-454b8a7e300f', defaultMessage: 'Categories' }), path: 'categories' },
        { id: 'integrations', label: defineMessage({ id: 'e0a5c374-8d62-4f19-9b34-2c7b6e0a1d85', defaultMessage: 'Integrations' }), path: 'integrations', flag: FeatureFlags.HOME_PREFERENCES_INTEGRATIONS },
        { id: 'billing', label: labels.billing, path: 'billing', flag: FeatureFlags.HOME_BILLING },
    ],
    explore: [
        { id: 'details', label: defineMessage({ id: 'a2e7b591-3d84-40c6-9f15-8b0c2d6e4a79', defaultMessage: 'Group details' }), path: 'details', flag: FeatureFlags.GROUP_PREFERENCES_DETAILS },
        { id: 'mine', label: defineMessage({ id: '7d0c4f83-1b96-4258-ae30-6f9b5c1d8e02', defaultMessage: 'My preferences' }), path: 'mine', flag: FeatureFlags.GROUP_PREFERENCES_MINE },
        { id: 'members', label: labels.members, path: 'members', flag: FeatureFlags.GROUP_PREFERENCES_MEMBERS },
        { id: 'billing', label: labels.billing, path: 'billing', flag: FeatureFlags.GROUP_BILLING },
    ],
};

export function groupSections(sections: NavSection[]): Array<{ group?: MessageDescriptor; items: NavSection[] }> {
    const out: Array<{ group?: MessageDescriptor; items: NavSection[] }> = [];

    for (const section of sections) {
        const existing = out[out.length - 1];

        if (
            existing &&
            (existing.group?.id === section.group?.id || (!existing.group && !section.group))
        ) {
            existing.items.push(section);
        } else {
            out.push({ ...(section.group ? { group: section.group } : {}), items: [section] });
        }
    }

    return out;
}

export const workspaceRoot = (stage: NavStage, id: string) =>
    stage === 'manage'
        ? route('homeWorkspace', { homeId: id })
        : route('group', { groupId: id });

export const sectionPath = (stage: NavStage, id: string, section: NavSection | NavSecondaryItem) => {
    if ('external' in section && section.external) return section.path;

    if (section.path.startsWith('/')) return section.path;
    const root = workspaceRoot(stage, id);
    return section.path ? `${root}/${section.path}` : root;
};
